import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import * as S from './CardSettings.styles';
import { BottomSheet } from '@/components/UI/BottomSheet/BottomSheet';
import { IconBox } from '@/components/UI/IconBox/IconBox.styles';
import { DrawerItem } from '@/components/Drawer/Drawer.styles';
import { useToggleOpenState } from '@/hooks/useToggleOpenState';
import { Button } from '@/components/UI/Button/Button';
import { Input } from '@/components/UI/Input/Input';
import {
  CARD_NAME_MAX_LENGTH,
  CardDataStat,
} from '@/components/Profile/Cards/Cards.constants';
import { SavedCard } from '@/api/types/cards.types';
import { ConfirmDeleteModal } from '@/features/BookmarkCollections/components';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { Modal } from '@/components/UI/Modal';
import { CloseIcon } from '@/components/UI/Icons/Icons';

export const renameCardFormSchema = (t: (key: string) => string) =>
  z.object({
    name: z
      .string()
      .trim()
      .min(1, t('pages.cabinet.card_name_min_symbols'))
      .max(CARD_NAME_MAX_LENGTH, t('pages.cabinet.card_name_max_symbols')),
  });

export type RenameCardFormFields = z.infer<
  ReturnType<typeof renameCardFormSchema>
>;

interface CardSettingsProps {
  isOpen: boolean;
  close: () => void;
  card: SavedCard;
  onRename: (name: string, onSuccess?: () => void) => void;
  onDelete: (onSuccess?: () => void) => void;
  onMakePrimary: (onSuccess?: () => void) => void;
  selectedAction?: 'menu' | 'rename' | 'remove';
  onActionStateReset?: () => void;
}

export const CardSettings: React.FC<CardSettingsProps> = ({
  isOpen,
  close,
  card,
  onRename,
  onDelete,
  onMakePrimary,
  selectedAction = 'menu',
  onActionStateReset,
}) => {
  const { t } = useTranslation();
  const { isDesktop } = useBreakpoint();
  const [isRemoveOpen, removeToggle] = useToggleOpenState();
  const [isRenameOpen, renameToggle] = useToggleOpenState();
  const prevSelectedAction = useRef<'menu' | 'rename' | 'remove'>('menu');
  const { control, handleSubmit, reset, formState } =
    useForm<RenameCardFormFields>({
      resolver: zodResolver(renameCardFormSchema(t)),
      defaultValues: { name: '' },
      mode: 'onChange',
    });

  const canMakePrimary = !card.isPrimary && card.isActive && !card.isExpired;
  const canRename = card.isActive && !card.isExpired;

  function startRemove() {
    close();
    removeToggle();
  }

  function closeRemoveModal() {
    removeToggle(false);
    onActionStateReset?.();
  }

  function remove() {
    onDelete(() => closeRemoveModal());
  }

  function startRename() {
    reset({ name: card.name });
    close();
    renameToggle();
  }

  function closeRenameModal() {
    renameToggle(false);
    onActionStateReset?.();
  }

  function rename(data: RenameCardFormFields) {
    onRename(data.name.trim(), () => closeRenameModal());
  }

  function makeMain() {
    onMakePrimary(close);
  }

  useEffect(() => {
    if (card) {
      reset({ name: card.name });
    }
  }, [card, reset]);

  useEffect(() => {
    if (prevSelectedAction.current === selectedAction) return;

    prevSelectedAction.current = selectedAction;

    if (selectedAction === 'rename') {
      reset({ name: card.name });
      renameToggle(true);
      return;
    }

    if (selectedAction === 'remove') {
      removeToggle(true);
    }
  }, [card.name, removeToggle, renameToggle, reset, selectedAction]);

  return (
    <>
      {isOpen && (
        <BottomSheet isOpen={isOpen} close={close} title={card.name}>
          {canMakePrimary && (
            <DrawerItem
              $isFirst
              onClick={makeMain}
              data-stat={CardDataStat.SetPrimaryCard}
            >
              <IconBox $filename="star.svg" $width={35} $height={35} />
              <S.Text $variant="1" as="span">
                {t('pages.cabinet.make_main')}
              </S.Text>
            </DrawerItem>
          )}

          {canRename && (
            <DrawerItem
              $isFirst={!canMakePrimary}
              onClick={startRename}
              data-stat={CardDataStat.RenameCard}
            >
              <IconBox $filename="edit.svg" $width={35} $height={35} />
              <S.Text $variant="1" as="span">
                {t('pages.cabinet.rename_card')}
              </S.Text>
            </DrawerItem>
          )}

          <DrawerItem
            $isFirst={!canMakePrimary && !canRename}
            $isLast
            onClick={startRemove}
            data-stat={CardDataStat.RemoveCard}
          >
            <IconBox $filename="trash.svg" $width={35} $height={35} />
            <S.Text $variant="1" as="span" $isRed>
              {t('pages.cabinet.remove_card')}
            </S.Text>
          </DrawerItem>
        </BottomSheet>
      )}

      {isDesktop ? (
        <Modal
          isOpen={isRenameOpen}
          onClose={closeRenameModal}
          maxWidth={460}
          dataTestId="rename-card-modal"
          isScrollLocked
        >
          <S.RenameModalBody>
            <S.RenameModalHeader>
              <S.RenameModalTitle>
                {t('pages.cabinet.rename_card')}
              </S.RenameModalTitle>
              <S.RenameModalCloseButton
                onClick={closeRenameModal}
                aria-label={t('common.deny')}
              >
                <CloseIcon />
              </S.RenameModalCloseButton>
            </S.RenameModalHeader>
            <S.Form onSubmit={handleSubmit(rename)}>
              <Controller
                name="name"
                control={control}
                render={({ field, fieldState }) => (
                  <Input
                    {...field}
                    appearance="filled"
                    label={t('pages.cabinet.card_name')}
                    helperText={fieldState.error?.message}
                    hasError={!!fieldState.error}
                    clear={() => field.onChange('')}
                  />
                )}
              />
              <Button
                type="submit"
                appearance="secondary"
                isFullWidth
                disabled={!formState.isValid}
              >
                {t('common.save')}
              </Button>
            </S.Form>
          </S.RenameModalBody>
        </Modal>
      ) : (
        <BottomSheet
          isOpen={isRenameOpen}
          close={closeRenameModal}
          title={t('pages.cabinet.rename_card')}
        >
          <S.Form onSubmit={handleSubmit(rename)}>
            <Controller
              name="name"
              control={control}
              render={({ field, fieldState }) => (
                <Input
                  {...field}
                  appearance="filled"
                  label={t('pages.cabinet.card_name')}
                  helperText={fieldState.error?.message}
                  hasError={!!fieldState.error}
                  clear={() => field.onChange('')}
                />
              )}
            />
            <Button
              type="submit"
              appearance="secondary"
              isFullWidth
              disabled={!formState.isValid}
            >
              {t('common.save')}
            </Button>
          </S.Form>
        </BottomSheet>
      )}

      <ConfirmDeleteModal
        isOpen={isRemoveOpen}
        onClose={closeRemoveModal}
        onSubmit={remove}
        title={t('pages.cabinet.are_you_sure_you_want_to_remove_saved_card')}
        description={null}
        confirmText={t('common.remove')}
        cancelText={t('common.deny')}
      />
    </>
  );
};
